import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import { FaUser, FaEnvelope, FaLock, FaLeaf, FaSpa, FaEye, FaEyeSlash, FaPhone, FaVenusMars, FaHome, FaCity, FaMapMarkerAlt } from 'react-icons/fa';
import yogBg from '../images/yog-bg.jpg';
import HttpInterceptor from '../lib/HttpInterceptor';

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const trainerId = searchParams.get('trainerId');
  // Get all programId values (can be multiple)
  const programIds = searchParams.getAll('programId');
  const [showPassword, setShowPassword] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  // Detect PWA standalone mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    window.addEventListener('resize', checkPWA);
    return () => window.removeEventListener('resize', checkPWA);
  }, []);

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be at most 100 characters')
      .matches(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes')
      .required('Name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .max(255, 'Email must be at most 255 characters')
      .required('Email is required'),
    mobile: Yup.string()
      .matches(/^[0-9]{10}$/, 'Enter a valid 10-digit mobile number')
      .required('Mobile number is required'),
    gender: Yup.string()
      .oneOf(['male', 'female', 'other'], 'Please select a valid gender')
      .required('Gender is required'),
    password: Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .max(12, 'Password must be at most 12 characters')
      .matches(/[a-zA-Z]/, 'Password must contain at least one letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .required('Password is required'),
    addressLine1: Yup.string()
      .trim()
      .required('Address Line 1 is required'),
    addressLine2: Yup.string()
      .trim(),
    city: Yup.string()
      .trim()
      .required('City is required'),
    state: Yup.string()
      .trim()
      .required('State is required'),
    pincode: Yup.string()
      .matches(/^[0-9]{6}$/, 'Enter a valid 6-digit pincode')
      .required('Pincode is required'),
    consentAccepted: Yup.boolean()
      .oneOf([true], 'You must accept the Terms of Service and Privacy Policy to create an account')
      .required('Acceptance of Terms of Service and Privacy Policy is required')
  });

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      // Sanitize input values before sending to backend
      const payload = {
        name: values.name.trim().substring(0, 100), // Trim and limit length
        email: values.email.trim().toLowerCase().substring(0, 255), // Normalize email
        mobile: values.mobile.replace(/[^\d]/g, '').substring(0, 10), // Only digits, max 10
        gender: values.gender, // Already validated via Yup enum
        password: values.password, // Password should not be trimmed/sanitized (may contain spaces intentionally)
        address: {
          addressLine1: values.addressLine1.trim(),
          addressLine2: values.addressLine2 ? values.addressLine2.trim() : '',
          city: values.city.trim(),
          state: values.state.trim(),
          pincode: values.pincode.trim(),
          country: 'India' // Defaulting to India
        },
        termsAccepted: values.consentAccepted,
        privacyAccepted: values.consentAccepted
      };

      // We handle signup errors locally (toast + inline message), so suppress global interceptor toasts for this request.
      const { data } = await HttpInterceptor.post('/auth/signup', payload, { showErrorToast: false });

      toast.success(data.message);
      const redirect = searchParams.get('redirect');

      // After successful signup, redirect to login with trainerId, programIds, and redirect param
      // The login page will then redirect to /student/services or the redirect path
      if (trainerId) {
        const sanitizedTrainerId = trainerId.replace(/[^a-zA-Z0-9]/g, '');
        const sanitizedProgramIds = programIds
          .map(id => id.replace(/[^a-zA-Z0-9]/g, ''))
          .filter(id => id.length > 0);

        if (sanitizedTrainerId) {
          const params = new URLSearchParams();
          params.set('trainerId', sanitizedTrainerId);
          sanitizedProgramIds.forEach(id => {
            params.append('programId', id);
          });
          if (redirect) {
            params.append('redirect', redirect);
          }
          navigate(`/login?${params.toString()}`);
        } else {
          navigate(redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login');
        }
      } else {
        navigate(redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Signup failed. Try again.';
      toast.error(msg);
      setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <section className={`min-h-screen flex items-center justify-center p-4 relative ${isPWA ? 'bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50' : ''}`}>
      {/* Background */}
      {!isPWA && (
        <div className="absolute inset-0 -z-10">
          <img src={yogBg} alt="Yoga background" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-emerald-900/80"></div>
        </div>
      )}

      {/* Signup Card */}
      <div className={`w-full ${isPWA ? 'max-w-lg' : 'max-w-md'} z-10`}>
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 border-2 border-teal-200/60 shadow-2xl' : 'bg-white shadow-xl'} rounded-xl sm:rounded-2xl overflow-hidden relative`}>
          {/* PWA Decorative Elements */}
          {isPWA && (
            <>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-teal-100/30 to-blue-100/20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-100/30 to-indigo-100/20 rounded-full blur-3xl"></div>
            </>
          )}

          {/* Header */}
          <div className={`bg-gradient-to-r from-teal-600 to-emerald-600 p-6 sm:p-8 text-center relative z-10`}>
            <div className={`${isPWA ? 'w-24 h-24 bg-white/20 backdrop-blur-sm ring-4 ring-white/30' : 'w-20 h-20 bg-white/10'} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
              <FaLeaf className={`${isPWA ? 'text-4xl' : 'text-3xl'} text-white rotate-45`} />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="relative">
                <div className="absolute left-0 w-1 h-5 bg-white/40 rounded-full blur-sm"></div>
                <div className="relative w-1 h-5 bg-white rounded-full"></div>
              </div>
              <h2 className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-2xl'} font-bold text-white drop-shadow-lg`}>
                Begin Your Wellness Journey
              </h2>
              <div className="relative">
                <div className="absolute left-0 w-1 h-5 bg-white/40 rounded-full blur-sm"></div>
                <div className="relative w-1 h-5 bg-white rounded-full"></div>
              </div>
            </div>
            <p className={`${isPWA ? 'text-base sm:text-lg' : 'text-sm'} text-white/90 font-medium`}>
              Create your Yogvital account
            </p>
          </div>

          {/* Form */}
          <Formik
            initialValues={{
              name: '',
              email: '',
              mobile: '',
              gender: '',
              password: '',
              addressLine1: '',
              addressLine2: '',
              city: '',
              state: '',
              pincode: '',
              consentAccepted: false
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values, setFieldValue }) => (
              <Form className={`${isPWA ? 'p-6 sm:p-8' : 'p-6'} space-y-4 sm:space-y-5 relative z-10`}>
                <ErrorMessage
                  name="submit"
                  component="div"
                  className={`${isPWA ? 'bg-red-50 border-2 border-red-200 text-red-700' : 'text-red-600'} text-sm text-center p-3 rounded-lg`}
                  role="alert"
                  aria-live="assertive"
                  id="submit-error"
                />

                {/* Name */}
                <div>
                  <label htmlFor="name" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium'} block mb-2`}>
                    Full Name
                    <span className="text-red-500 ml-1" aria-label="required">*</span>
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                      <FaUser className={`${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                    </div>
                    <Field
                      name="name"
                      id="name"
                      type="text"
                      placeholder="Your full name"
                      className={`${isPWA ? 'pl-12 pr-4 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                      aria-required="true"
                      aria-describedby="name-error"
                      autoComplete="name"
                    />
                  </div>
                  <ErrorMessage
                    name="name"
                    component="p"
                    id="name-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                    aria-live="polite"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium'} block mb-2`}>
                    Email Address
                    <span className="text-red-500 ml-1" aria-label="required">*</span>
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                      <FaEnvelope className={`${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                    </div>
                    <Field
                      name="email"
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      className={`${isPWA ? 'pl-12 pr-4 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                      aria-required="true"
                      aria-describedby="email-error"
                      autoComplete="email"
                    />
                  </div>
                  <ErrorMessage
                    name="email"
                    component="p"
                    id="email-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                    aria-live="polite"
                  />
                </div>

                {/* Mobile */}
                <div>
                  <label htmlFor="mobile" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium'} block mb-2`}>
                    Mobile Number
                    <span className="text-red-500 ml-1" aria-label="required">*</span>
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                      <FaPhone className={`${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                    </div>
                    <Field
                      name="mobile"
                      id="mobile"
                      type="tel"
                      placeholder="9999999999"
                      className={`${isPWA ? 'pl-12 pr-4 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                      aria-required="true"
                      aria-describedby="mobile-error"
                      autoComplete="tel"
                    />
                  </div>
                  <ErrorMessage
                    name="mobile"
                    component="p"
                    id="mobile-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                    aria-live="polite"
                  />
                </div>

                {/* Gender */}
                <div>
                  <label htmlFor="gender" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium'} block mb-2`}>
                    Gender
                    <span className="text-red-500 ml-1" aria-label="required">*</span>
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none z-10`} aria-hidden="true">
                      <FaVenusMars className={`${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                    </div>
                    <Field
                      as="select"
                      name="gender"
                      id="gender"
                      className={`${isPWA ? 'pl-12 pr-4 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 border bg-white py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300 appearance-none`}
                      aria-required="true"
                      aria-describedby="gender-error"
                      autoComplete="sex"
                    >
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Others</option>
                    </Field>
                  </div>
                  <ErrorMessage
                    name="gender"
                    component="p"
                    id="gender-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                    aria-live="polite"
                  />
                </div>

                {/* Address Section */}
                <div className="space-y-4 sm:space-y-5 pt-2">
                  <h3 className={`${isPWA ? 'text-lg font-bold text-teal-800' : 'text-base font-semibold text-gray-700'} border-b pb-1`}>Address Details</h3>

                  {/* Address Line 1 */}
                  <div>
                    <label htmlFor="addressLine1" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium'} block mb-2`}>
                      Address Line 1
                      <span className="text-red-500 ml-1" aria-label="required">*</span>
                    </label>
                    <div className="relative">
                      <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                        <FaHome className={`${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                      </div>
                      <Field
                        name="addressLine1"
                        id="addressLine1"
                        type="text"
                        placeholder="House No., Building Name"
                        className={`${isPWA ? 'pl-12 pr-4 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                        aria-required="true"
                        aria-describedby="addressLine1-error"
                        autoComplete="address-line1"
                      />
                    </div>
                    <ErrorMessage
                      name="addressLine1"
                      component="p"
                      id="addressLine1-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                      aria-live="polite"
                    />
                  </div>

                  {/* Address Line 2 */}
                  <div>
                    <label htmlFor="addressLine2" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium'} block mb-2`}>
                      Address Line 2 (Optional)
                    </label>
                    <div className="relative">
                      <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                        <FaHome className={`${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                      </div>
                      <Field
                        name="addressLine2"
                        id="addressLine2"
                        type="text"
                        placeholder="Street, Area"
                        className={`${isPWA ? 'pl-12 pr-4 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                        aria-describedby="addressLine2-error"
                        autoComplete="address-line2"
                      />
                    </div>
                    <ErrorMessage
                      name="addressLine2"
                      component="p"
                      id="addressLine2-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                      aria-live="polite"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* City */}
                    <div className="flex-1">
                      <label htmlFor="city" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium'} block mb-2`}>
                        City
                        <span className="text-red-500 ml-1" aria-label="required">*</span>
                      </label>
                      <div className="relative">
                        <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                          <FaCity className={`${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                        </div>
                        <Field
                          name="city"
                          id="city"
                          type="text"
                          placeholder="City"
                          className={`${isPWA ? 'pl-12 pr-4 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                          aria-required="true"
                          aria-describedby="city-error"
                          autoComplete="address-level2"
                        />
                      </div>
                      <ErrorMessage
                        name="city"
                        component="p"
                        id="city-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                        aria-live="polite"
                      />
                    </div>

                    {/* State */}
                    <div className="flex-1">
                      <label htmlFor="state" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium'} block mb-2`}>
                        State
                        <span className="text-red-500 ml-1" aria-label="required">*</span>
                      </label>
                      <div className="relative">
                        <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                          <FaMapMarkerAlt className={`${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                        </div>
                        <Field
                          name="state"
                          id="state"
                          type="text"
                          placeholder="State"
                          className={`${isPWA ? 'pl-12 pr-4 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                          aria-required="true"
                          aria-describedby="state-error"
                          autoComplete="address-level1"
                        />
                      </div>
                      <ErrorMessage
                        name="state"
                        component="p"
                        id="state-error"
                        className="text-red-500 text-xs mt-1"
                        role="alert"
                        aria-live="polite"
                      />
                    </div>
                  </div>

                  {/* Pincode */}
                  <div>
                    <label htmlFor="pincode" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium'} block mb-2`}>
                      Pincode
                      <span className="text-red-500 ml-1" aria-label="required">*</span>
                    </label>
                    <div className="relative">
                      <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                        <FaMapMarkerAlt className={`${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                      </div>
                      <Field
                        name="pincode"
                        id="pincode"
                        type="text"
                        placeholder="6-digit Pincode"
                        maxLength="6"
                        className={`${isPWA ? 'pl-12 pr-4 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                        aria-required="true"
                        aria-describedby="pincode-error"
                        autoComplete="postal-code"
                      />
                    </div>
                    <ErrorMessage
                      name="pincode"
                      component="p"
                      id="pincode-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                      aria-live="polite"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium'} block mb-2`}>
                    Password
                    <span className="text-red-500 ml-1" aria-label="required">*</span>
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                      <FaLock className={`${isPWA ? 'text-teal-500' : 'text-gray-400'}`} />
                    </div>
                    <Field
                      name="password"
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="6-12 characters"
                      className={`${isPWA ? 'pl-12 pr-12 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 pr-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                      aria-required="true"
                      aria-describedby="password-error"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute inset-y-0 right-0 ${isPWA ? 'pr-4' : 'pr-3'} flex items-center ${isPWA ? 'text-teal-500 hover:text-teal-600' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                    </button>
                  </div>
                  <ErrorMessage
                    name="password"
                    component="p"
                    id="password-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                    aria-live="polite"
                  />
                </div>

                {/* Consent Checkbox */}
                <div className={`${isPWA ? 'pt-3' : 'pt-2'}`}>
                  <div className="flex items-start">
                    <Field
                      type="checkbox"
                      name="consentAccepted"
                      id="consentAccepted"
                      className={`${isPWA ? 'mt-1.5 h-5 w-5 text-teal-600 border-2 border-teal-300 rounded-md focus:ring-2 focus:ring-teal-300/50' : 'mt-1 h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500'}`}
                    />
                    <label htmlFor="consentAccepted" className={`${isPWA ? 'ml-3 text-sm sm:text-base text-teal-800' : 'ml-2 text-sm text-gray-700'}`}>
                      I accept the{' '}
                      <Link
                        to="/terms-of-service"
                        target="_blank"
                        className={`${isPWA ? 'text-teal-600 hover:text-teal-700 font-semibold' : 'text-teal-600 hover:text-teal-700'} underline`}
                      >
                        Terms of Service
                      </Link>
                      {' '}and{' '}
                      <Link
                        to="/privacy-policy"
                        target="_blank"
                        className={`${isPWA ? 'text-teal-600 hover:text-teal-700 font-semibold' : 'text-teal-600 hover:text-teal-700'} underline`}
                      >
                        Privacy Policy
                      </Link>
                      <span className="text-red-500 ml-1" aria-label="required">*</span>
                    </label>
                  </div>
                  <ErrorMessage
                    name="consentAccepted"
                    component="p"
                    className={`text-red-500 text-xs ${isPWA ? 'ml-8 mt-1' : 'ml-6 mt-1'}`}
                    role="alert"
                    aria-live="polite"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full ${isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 hover:from-teal-600 hover:via-blue-600 hover:to-indigo-600 py-3.5 px-6 rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl' : 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 py-2 px-4 rounded-lg shadow-md hover:shadow-lg'} text-white font-bold transition-all duration-300 flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-[0.98]'
                    }`}
                >
                  <FaSpa className="text-white" />
                  <span>{isSubmitting ? 'Creating Account...' : 'Create Account'}</span>
                </button>

                {/* Footer Links */}
                <div className={`text-center ${isPWA ? 'pt-5 border-t-2 border-teal-200/50' : 'pt-4 border-t border-gray-200'}`}>
                  <p className={`${isPWA ? 'text-sm sm:text-base text-teal-700' : 'text-sm text-gray-600'}`}>
                    Already have an account?{' '}
                    <Link 
                      to={`/login${location.search}`} 
                      className={`${isPWA ? 'font-bold text-teal-600 hover:text-teal-700 underline' : 'font-medium text-teal-600 hover:text-teal-500'}`}
                    >
                      Sign In
                    </Link>
                  </p>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default Signup;
